'use client';
import {
	type ChangeEventHandler,
	type ComponentProps,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from 'react';
import styled from 'styled-components';

import { useRTL } from '../../../../hooks/helpers/useRTL';
import { Label } from '../../../atoms/label/Label';
import { BaseTextInput } from '../text-input/BaseTextInput';

export type BaseCodeInputRef = {
	blur: () => void;
	focus: () => void;
};

export type BaseCodeInputProps = {
	autoComplete?: ComponentProps<typeof BaseTextInput>['autoComplete'];
	className?: string;
	codeLength: number;
	disabled?: boolean;
	hasError?: boolean;
	name: string;
	onChange?: (code: string) => void;
	onCodeComplete?: (code: string) => void;
	onKeyDown?: ComponentProps<typeof BaseTextInput>['onKeyDown'];
	onOvershoot?: (x: string) => void;
	pattern?: ComponentProps<typeof BaseTextInput>['pattern'];
	testID?: string;
	type?: ComponentProps<typeof BaseTextInput>['type'];
	value?: string;
};

export const BaseCodeInput = forwardRef<BaseCodeInputRef, BaseCodeInputProps>(
	(
		{
			autoComplete = 'off',
			className,
			codeLength,
			disabled,
			hasError,
			name,
			onChange,
			onCodeComplete,
			onKeyDown,
			onOvershoot,
			pattern,
			testID,
			type,
			value = ''
		},
		ref
	) => {
		const [isFocused, setIsFocused] = useState<boolean>(true);
		const inputRef = useRef<HTMLInputElement>(null);
		const { forcedLtrFlexDirection } = useRTL();

		const onPress = useCallback(() => {
			inputRef.current?.focus();
			setIsFocused(true);
		}, [inputRef, setIsFocused]);

		useEffect(() => {
			if (!disabled) setTimeout(() => onPress(), 0);
		}, [disabled, onPress]);

		useImperativeHandle(
			ref,
			() => ({
				blur: () => {
					inputRef.current?.blur();
				},
				focus: () => {
					inputRef.current?.focus();
				}
			}),
			[]
		);

		const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
			codeInput => {
				if (codeInput.target.value.length > codeLength) return onOvershoot?.(codeInput.target.value);
				onChange?.(codeInput.target.value);
				if (codeInput.target.value.length === codeLength) {
					onCodeComplete?.(codeInput.target.value);
				}
			},
			[onChange, onCodeComplete, codeLength, onOvershoot]
		);

		return (
			<S.CodeInputWrapper
				$disabled={!!disabled}
				$flexDirection={forcedLtrFlexDirection ?? 'row'}
				className={className}
				data-testid={testID}
				onClick={onPress}
			>
				<S.Input
					autoComplete={autoComplete}
					autoFocus
					disabled={disabled}
					name={name}
					pattern={pattern}
					ref={inputRef}
					testID={`${testID}-field`}
					type={type}
					value={value}
					onBlur={() => setIsFocused(false)}
					onChange={handleChange}
					onFocus={() => setIsFocused(true)}
					onKeyDown={onKeyDown}
				/>

				{Array.from({ length: codeLength }, (_, i) => (
					<S.CodeBox
						$active={i === value.length && isFocused}
						$disabled={disabled}
						$filled={i < value.length}
						$hasError={!!hasError}
						key={i}
					>
						{i < value.length ? (
							<S.Code>{value[i]}</S.Code>
						) : i > value.length || !isFocused ? (
							<S.EmptyCode>0</S.EmptyCode>
						) : null}
					</S.CodeBox>
				))}
			</S.CodeInputWrapper>
		);
	}
);

BaseCodeInput.displayName = 'BaseCodeInput';

namespace S {
	export const CodeInputWrapper = styled.div<{ $disabled: boolean; $flexDirection: string }>`
		display: flex;
		flex-direction: ${({ $flexDirection }) => $flexDirection};
		position: relative;
		cursor: pointer;
		${({ $disabled }) => ($disabled ? 'pointer-events: none;' : '')};
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;
	export const Input = styled(BaseTextInput)`
		opacity: 0;
		position: absolute;
	`;

	export const CodeBox = styled.div<{
		$active: boolean;
		$disabled?: boolean;
		$filled: boolean;
		$hasError: boolean;
	}>`
		width: 52px;
		height: 68px;
		border-style: solid;
		border-width: ${({ theme }) => theme.border.width.medium};
		border-color: ${({ $active, $filled, $hasError, theme }) =>
			$hasError
				? theme.colors.borders.error.enabled
				: $active || $filled
					? theme.colors.borders.brand.enabled
					: 'transparent'};
		justify-content: center;
		align-items: center;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.input};
		${({ theme }) => theme.border.radius.medium};
		opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
		color: ${({ theme }) => theme.colors.text.onDark.enabled};
	`;

	export const Code = styled(Label)`
		width: 100%;
		height: 100%;
		${({ theme }) => theme.fonts.headings.title2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.input};
	`;

	export const EmptyCode = styled(Code)`
		color: ${({ theme }) => theme.colors.text.main.disabled};
	`;
}
